'use client';

import React from 'react';
import type { Key } from '@react-types/shared';
import type { MenuProps as AriaMenuProps } from 'react-aria-components';
import { Menu as AriaMenu } from 'react-aria-components';
import { cx } from '../../../utils/cx';
import { MenuFooter } from './MenuFooter';
import { MenuHeaderBase, MenuHeader } from './MenuHeader';
import { MenuItem, MenuItemStructuredBase } from './MenuItem';
import { CustomMenuItem } from './CustomMenuItem';
import { MenuItemBase } from './MenuItemBase';
import { MenuSeparatorBase, MenuSeparator } from './MenuSeparator';
import { MenuSubmenu, MenuSubmenuPopover } from './MenuSubmenu';
import type { MenuSize } from './menuTypes';
import { MenuContext } from './menuContext';
import { MenuSubmenuContext } from './menuSubmenuContext';
import type { MenuSubmenuTriggerMode } from './menuSubmenuContext';

const MENU_DISPLAY_NAME = 'RomaMenu';
const MENU_COLLECTION_DISPLAY_NAME = 'RomaMenuCollection';

export type MenuProps<T extends object = object> = Omit<AriaMenuProps<T>, 'className'> & {
    size?: MenuSize;
    className?: AriaMenuProps<T>['className'];
};

const MenuShell = <T extends object>({ size = 'sm', className, ...props }: MenuProps<T>): JSX.Element => {
    const menuRef = React.useRef<HTMLDivElement>(null);
    const submenuTriggerModesRef = React.useRef(new Map<Key, MenuSubmenuTriggerMode>());
    const getSubmenuTriggerMode = React.useCallback(
        (key: Key | undefined) => (key === undefined ? undefined : submenuTriggerModesRef.current.get(key)),
        [],
    );
    const registerSubmenuTriggerMode = React.useCallback((key: Key, mode: MenuSubmenuTriggerMode) => {
        submenuTriggerModesRef.current.set(key, mode);

        return () => {
            submenuTriggerModesRef.current.delete(key);
        };
    }, []);
    const submenuRegistry = React.useMemo(
        () => ({ getSubmenuTriggerMode, registerSubmenuTriggerMode }),
        [getSubmenuTriggerMode, registerSubmenuTriggerMode],
    );

    return (
        <MenuContext.Provider value={{ size, menuRef }}>
            <MenuSubmenuContext.Provider value={submenuRegistry}>
                <AriaMenu
                    {...props}
                    ref={menuRef}
                    className={(state) =>
                        cx(
                            MENU_COLLECTION_DISPLAY_NAME,
                            'w-full outline-hidden',
                            typeof className === 'function' ? className(state) : className,
                        )
                    }
                />
            </MenuSubmenuContext.Provider>
        </MenuContext.Provider>
    );
};

MenuShell.displayName = MENU_DISPLAY_NAME;

type MenuBaseComponents = {
    Item: typeof MenuItemBase;
    ItemStructured: typeof MenuItemStructuredBase;
    Header: typeof MenuHeaderBase;
    Separator: typeof MenuSeparatorBase;
};

export const Menu = MenuShell as typeof MenuShell & {
    Base: MenuBaseComponents;
    Header: typeof MenuHeader;
    Footer: typeof MenuFooter;
    Item: typeof MenuItem;
    CustomItem: typeof CustomMenuItem;
    Separator: typeof MenuSeparator;
    Submenu: typeof MenuSubmenu;
    SubmenuPopover: typeof MenuSubmenuPopover;
};

Menu.Base = {
    Item: MenuItemBase,
    ItemStructured: MenuItemStructuredBase,
    Header: MenuHeaderBase,
    Separator: MenuSeparatorBase,
};
Menu.Header = MenuHeader;
Menu.Footer = MenuFooter;
Menu.Item = MenuItem;
Menu.CustomItem = CustomMenuItem;
Menu.Separator = MenuSeparator;
Menu.Submenu = MenuSubmenu;
Menu.SubmenuPopover = MenuSubmenuPopover;

export type { MenuFooterProps } from './MenuFooter';
export type { MenuHeaderBaseProps, MenuHeaderProps } from './MenuHeader';
export type { MenuItemStructuredBaseProps, MenuItemProps } from './MenuItem';
export type { MenuItemRootBaseProps } from './MenuItemBase';
export type { CustomMenuItemProps } from './CustomMenuItem';
export type { MenuSeparatorBaseProps, MenuSeparatorProps } from './MenuSeparator';
export type { MenuSubmenuProps, MenuSubmenuPopoverProps } from './MenuSubmenu';
